import React from 'react';
import { StatusChip, type DataTableColumn } from '@routes-plan/ui';
import { useT } from '../../../i18n';
import { formatCurrency } from '../../../utils/format/currency';

/** Fila de un movimiento de caja (gastos de ruta y movimientos de caja principal). */
export interface MovementRow {
  id: number;
  expenseName: string;
  operationTypeId?: number;
  operationTypeName: string;
  routeId: number | null;
  routeName: string;
  value: number;
  description: string;
  date: string;
  createdAt?: string | Date;
}

type T = ReturnType<typeof useT>;

/** Tipo de operación 1 suma a la caja; 2 resta. */
const isIncome = (row: MovementRow) => Number(row.operationTypeId) === 1;

/** Columnas comunes de los listados de movimientos. */
export const movementColumns = (t: T, formatDate: (row: MovementRow) => string): DataTableColumn<MovementRow>[] => [
  { id: 'expenseName', header: t('expenses.expenseType'), value: (row) => row.expenseName },
  { id: 'date', header: t('common.date'), value: (row) => formatDate(row), hideBelow: 'sm' },
  { id: 'routeName', header: t('common.route'), value: (row) => row.routeName },
  {
    id: 'operationTypeName',
    header: t('expenses.operation'),
    value: (row) => row.operationTypeName,
    hideBelow: 'md',
    render: (row) => <StatusChip label={row.operationTypeName} tone={isIncome(row) ? 'success' : 'warning'} />,
  },
  { id: 'description', header: t('expenses.description'), value: (row) => row.description, wrap: true, width: 240, hideBelow: 'md' },
  {
    id: 'value',
    header: t('expenses.value'),
    align: 'right',
    value: (row) => Number(row.value ?? 0),
    render: (row) => formatCurrency(row.value),
  },
];

/** Totales de entradas (suman) y salidas (restan) de las filas visibles. */
export const movementTotals = (rows: MovementRow[]) =>
  rows.reduce(
    (acc, row) => {
      const value = Number(row.value) || 0;
      if (isIncome(row)) acc.in += value;
      else if (Number(row.operationTypeId) === 2) acc.out += value;
      return acc;
    },
    { in: 0, out: 0 },
  );
